import { useRef, useState, useEffect, useCallback } from "react";

/**
 * Spamabwehr in drei Schichten.
 *
 * 1. Honigtopf — ein Eingabefeld, das im Browser nicht zu sehen ist.
 *    Ein Mensch kann es nicht ausfuellen, viele Bots fuellen stur jedes
 *    Feld, das sie im Markup finden.
 * 2. Zeitschwelle — wie lange zwischen Seitenaufbau und Absenden verging.
 *    Bots senden binnen Millisekunden, Menschen brauchen Sekunden.
 * 3. reCAPTCHA v3 — schliesst die Luecke, die die ersten beiden offen
 *    lassen: Ein Bot, der den Endpunkt direkt anspricht, kann Honigtopf
 *    und Zeitangabe einfach weglassen. Ein Token laesst sich nicht
 *    weglassen, es wird serverseitig bei Google geprueft.
 *
 * Ausgewertet wird alles im Backend. Hier wird nur erhoben.
 *
 * Ohne `REACT_APP_RECAPTCHA_SITE_KEY` bleibt Schicht 3 aus und es wird
 * kein einziges Byte an Google geschickt.
 *
 * Verwendung:
 *
 *   const { honigtopf, spamFelder } = useSpamSchutz("kontakt");
 *   ...
 *   <Honigtopf {...honigtopf} />
 *   ...
 *   body: JSON.stringify({ ...formular, ...(await spamFelder()) })
 */

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

/* Das Skript wird hoechstens einmal geladen, egal wie viele Formulare
   auf der Seite stehen. Und nur dort, wo ein Formular haengt - nicht
   auf jeder Seite der Website. */
let ladeVorgang = null;

function recaptchaLaden() {
  if (!SITE_KEY) return Promise.resolve(false);
  if (ladeVorgang) return ladeVorgang;

  ladeVorgang = new Promise((fertig) => {
    if (window.grecaptcha) return fertig(true);
    const skript = document.createElement("script");
    skript.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    skript.async = true;
    skript.defer = true;
    skript.onload = () => fertig(true);
    skript.onerror = () => fertig(false);
    document.head.appendChild(skript);
  });
  return ladeVorgang;
}

async function recaptchaToken(aktion) {
  const bereit = await recaptchaLaden();
  if (!bereit || !window.grecaptcha) return undefined;
  try {
    await new Promise((fertig) => window.grecaptcha.ready(fertig));
    return await window.grecaptcha.execute(SITE_KEY, { action: aktion });
  } catch {
    /* Faellt das Captcha aus, bleiben Honigtopf und Zeitschwelle.
       Eine echte Anfrage darf daran nicht scheitern. */
    return undefined;
  }
}

export function useSpamSchutz(aktion = "formular") {
  const geladenUm = useRef(Date.now());
  const [wert, setWert] = useState("");

  useEffect(() => {
    recaptchaLaden();
  }, []);

  const spamFelder = useCallback(async () => {
    const felder = {
      website: wert,
      ladeZeitMs: Date.now() - geladenUm.current,
    };
    const token = await recaptchaToken(aktion);
    if (token) felder.recaptchaToken = token;
    return felder;
  }, [wert, aktion]);

  return { honigtopf: { wert, setWert }, spamFelder };
}

/**
 * Bewusst nicht `display:none` und nicht `hidden`: Beides erkennen viele
 * Bots und lassen das Feld dann in Ruhe. Aus dem Sichtfeld geschoben
 * wirkt es fuer sie wie ein normales Feld.
 *
 * `tabIndex={-1}` haelt es aus der Tastaturreihenfolge, `aria-hidden`
 * aus der Sprachausgabe. Ein Mensch kann es also weder sehen noch
 * ansteuern noch vorgelesen bekommen.
 */
export function Honigtopf({ wert, setWert }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        left: "-9999px",
        top: "auto",
        width: "1px",
        height: "1px",
        overflow: "hidden",
      }}
    >
      <label htmlFor="website-url">Bitte dieses Feld leer lassen</label>
      <input
        type="text"
        id="website-url"
        name="website"
        value={wert}
        onChange={(e) => setWert(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
      />
    </div>
  );
}
